import { IonContent, IonPage } from '@ionic/react';
import React, { useCallback, useEffect, useState } from 'react';
import { useAdminOverview, type AdminOverview } from '../api/admin';
import { useApi } from '../api/client';
import { ChanceyCard } from '../components/chancey';
import { safeAreaPadding } from '../components/layout/safeArea';
import {
  getPushDebugState,
  getPushPlatform,
  hasBannerPermission,
  subscribeToForegroundNotifications,
  type PushDebugState,
} from '../services/pushService';
import { useChanceyTheme } from '../theme/ThemeContext';

const AdminPage: React.FC = () => {
  const { c, d } = useChanceyTheme();
  const { data, loading, error, reload } = useAdminOverview();

  return (
    <IonPage>
      <IonContent fullscreen style={{ '--background': c.bg }}>
        <div
          className="flex flex-col"
          style={{ ...safeAreaPadding(8, d.pad, 16, d.pad), gap: d.gap, color: c.ink }}
        >
          <Header data={data} loading={loading} onRefresh={() => reload(true)} />
          {error && <ErrorCard error={error} onRetry={() => reload()} />}
          {!data && !error && loading && <LoadingRow />}
          {data && <Body data={data} />}
          <PushTestCard />
          {data && <Footer data={data} />}
        </div>
      </IonContent>
    </IonPage>
  );
};

/**
 * Convenience "verify delivery" surface. Hits the same /v1/me/push/test-self
 * endpoint any signed-in user can call — the AdminPage is just the typical
 * place support staff will check delivery from. Rate limit is enforced
 * server-side (see pushRoutes.ts).
 */
const PushTestCard: React.FC = () => {
  const { c, typ } = useChanceyTheme();
  const api = useApi();
  const [debug, setDebug] = useState<PushDebugState | null>(null);
  const [state, setState] = useState<
    | { kind: 'idle' }
    | { kind: 'busy' }
    | { kind: 'done'; msg: string; detail?: string }
    | { kind: 'error'; msg: string }
  >({ kind: 'idle' });

  const refreshDebug = useCallback(async () => {
    try {
      setDebug(await getPushDebugState());
    } catch {
      /* best-effort diagnostics only */
    }
  }, []);

  useEffect(() => {
    void refreshDebug();
    return subscribeToForegroundNotifications(() => {
      void refreshDebug();
    });
  }, [refreshDebug]);

  const onSend = async () => {
    if (state.kind === 'busy') return;
    setState({ kind: 'busy' });
    try {
      let latestDebug = debug;
      try {
        latestDebug = await getPushDebugState();
        setDebug(latestDebug);
      } catch {
        /* Send can still run; server will report missing subscription matches. */
      }
      const subscriptionId =
        latestDebug?.subscriptionId &&
        latestDebug.subscriptionTokenPresent &&
        latestDebug.optedIn !== false
          ? latestDebug.subscriptionId
          : null;
      const res = await api.post<{
        ok: boolean;
        notificationId?: string | null;
        dedupeHit?: boolean;
        noRecipients?: boolean;
        identitySync?: 'ok' | 'failed' | 'skipped';
        tagSync?: 'ok' | 'failed' | 'skipped';
        identityStatus?: number | null;
        oneSignalStatus?: number | null;
      }>(
        '/v1/me/push/test-self',
        {
          platform: getPushPlatform(),
          ...(subscriptionId ? { subscriptionId } : {}),
        }
      );
      await refreshDebug();
      setState({
        kind: 'done',
        msg: res.noRecipients
          ? 'No subscribed device matched your account yet.'
          : res.dedupeHit
            ? 'Duplicate within this minute — try again in 60s.'
            : 'Sent. Check your device.',
        detail: [
          res.notificationId ? `notification ${res.notificationId}` : null,
          res.identitySync && res.identitySync !== 'skipped' ? `identity sync ${res.identitySync}` : null,
          res.tagSync ? `tag sync ${res.tagSync}` : null,
          typeof res.identityStatus === 'number' && res.identityStatus > 0 ? `identity ${res.identityStatus}` : null,
          typeof res.oneSignalStatus === 'number' ? `OneSignal ${res.oneSignalStatus}` : null,
        ].filter(Boolean).join(' · '),
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Send failed';
      await refreshDebug();
      setState({ kind: 'error', msg });
    }
  };

  const apiBase = process.env.REACT_APP_API_BASE_URL || 'unset';
  const clerkKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY || '';
  const clerkTier = clerkKey.startsWith('pk_live_')
    ? 'live'
    : clerkKey.startsWith('pk_test_')
      ? 'test'
      : clerkKey
        ? 'unknown'
        : 'unset';
  const osApp = process.env.REACT_APP_ONESIGNAL_APP_ID || '';
  const permissionStatus = debug?.permissionStatus ?? 'unknown';
  const rows: Array<[string, string]> = [
    ['API', apiBase],
    ['Clerk', clerkTier],
    ['OneSignal', osApp ? `…${osApp.slice(-8)}` : 'off'],
    ['Platform', debug?.platform ?? 'loading'],
    [
      'Permission',
      `${permissionStatus.replace('_', ' ')}${
        hasBannerPermission(permissionStatus) ? '' : ' (no banners)'
      }`,
    ],
    [
      'Native perm',
      debug?.permissionNative === null || debug?.permissionNative === undefined
        ? 'n/a'
        : String(debug.permissionNative),
    ],
    [
      'Can prompt',
      debug?.canRequestPermission === null || debug?.canRequestPermission === undefined
        ? 'unknown'
        : debug.canRequestPermission
          ? 'yes'
          : 'no',
    ],
    ...(debug?.iosSettings
      ? ([
          ['iOS Alerts', debug.iosSettings.alertSetting ?? 'unknown'],
          ['iOS Sounds', debug.iosSettings.soundSetting ?? 'unknown'],
          ['iOS Center', debug.iosSettings.notificationCenterSetting ?? 'unknown'],
          ['iOS Lock', debug.iosSettings.lockScreenSetting ?? 'unknown'],
          ['iOS Summary', debug.iosSettings.scheduledDeliverySetting ?? 'unknown'],
          ['Delivered', String(debug.iosSettings.deliveredCount ?? 0)],
        ] satisfies Array<[string, string]>)
      : []),
    ['External ID', debug?.externalId ?? debug?.currentUserId ?? 'missing'],
    ['Subscription', debug?.subscriptionId ? `…${debug.subscriptionId.slice(-8)}` : 'missing'],
    ['Token', debug?.subscriptionTokenPresent ? 'present' : 'missing'],
    ['Opted in', debug?.optedIn === null || debug?.optedIn === undefined ? 'unknown' : debug.optedIn ? 'yes' : 'no'],
    [
      'Foreground',
      debug?.lastForeground
        ? `${debug.lastForeground.title ?? 'push'} at ${new Date(debug.lastForeground.receivedAt).toLocaleTimeString()}`
        : 'none observed',
    ],
  ];

  return (
    <ChanceyCard c={c} pad={14}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div
            className="text-[13px] font-semibold"
            style={{ color: c.ink, fontFamily: typ.body }}
          >
            Push delivery check
          </div>
          <div
            className="mt-0.5 text-[11px]"
            style={{ color: c.inkMuted, fontFamily: typ.body }}
          >
            Sends a test notification to your own device(s). Rate-limited per user.
          </div>
          {state.kind === 'done' && (
            <div
              className="mt-1.5 text-[11px]"
              style={{ color: c.accent, fontFamily: typ.body }}
            >
              {state.msg}
              {state.detail ? ` ${state.detail}` : ''}
            </div>
          )}
          {state.kind === 'error' && (
            <div
              className="mt-1.5 text-[11px]"
              style={{ color: c.lucky, fontFamily: typ.body }}
            >
              {state.msg}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => void onSend()}
          disabled={state.kind === 'busy'}
          className="shrink-0 rounded-full border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.06em]"
          style={{
            borderColor: c.line,
            color: c.ink,
            background: c.surfaceAlt,
            fontFamily: typ.body,
            opacity: state.kind === 'busy' ? 0.5 : 1,
          }}
        >
          {state.kind === 'busy' ? 'Sending…' : 'Send test'}
        </button>
      </div>
      <div
        className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 border-t pt-3 text-[10px]"
        style={{ borderColor: c.line, color: c.inkMuted, fontFamily: typ.body }}
      >
        {rows.map(([label, value]) => (
          <React.Fragment key={label}>
            <div className="font-semibold uppercase tracking-[0.06em]">{label}</div>
            <div className="min-w-0 break-words text-right">{value}</div>
          </React.Fragment>
        ))}
      </div>
    </ChanceyCard>
  );
};

const Header: React.FC<{
  data: AdminOverview | null;
  loading: boolean;
  onRefresh: () => void;
}> = ({ data, loading, onRefresh }) => {
  const { c, typ } = useChanceyTheme();
  return (
    <div className="flex items-start justify-between gap-3 px-0.5 pt-1">
      <div>
        <div
          className="text-xs font-medium uppercase tracking-[0.08em]"
          style={{ color: c.inkMuted, fontFamily: typ.body }}
        >
          Internal tools
        </div>
        <div
          className="mt-0.5 text-[32px] leading-tight"
          style={{
            fontFamily: typ.display,
            fontWeight: typ.displayWeight,
            letterSpacing: typ.displayLetter,
          }}
        >
          Admin
        </div>
        {data && (
          <div
            className="mt-1 text-[11px]"
            style={{ color: c.inkMuted, fontFamily: typ.body }}
          >
            Generated {relativeTime(data.generatedAt)}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onRefresh}
        disabled={loading}
        className="shrink-0 rounded-full border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.06em]"
        style={{
          borderColor: c.line,
          color: c.ink,
          background: c.surfaceAlt,
          fontFamily: typ.body,
          opacity: loading ? 0.5 : 1,
        }}
      >
        {loading ? 'Loading…' : 'Refresh'}
      </button>
    </div>
  );
};

const Body: React.FC<{ data: AdminOverview }> = ({ data }) => {
  const { c, typ } = useChanceyTheme();
  const { allTime, errorsByCode, last30Days, topUsers } = data;
  const successRate = allTime.scans
    ? Math.round((allTime.okScans / allTime.scans) * 100)
    : 0;
  const avgCost = allTime.scans ? allTime.costUsd / allTime.scans : 0;
  const last7Cost = last30Days.slice(-7).reduce((s, d) => s + d.costUsd, 0);
  const totalErrors = errorsByCode.reduce((s, e) => s + e.count, 0);

  return (
    <>
      <ChanceyCard c={c} pad={18}>
        <div
          className="text-[11px] font-semibold uppercase tracking-[0.08em]"
          style={{ color: c.inkMuted, fontFamily: typ.body }}
        >
          Total API cost
        </div>
        <div
          className="mt-1.5 text-[34px] leading-none"
          style={{
            fontFamily: typ.display,
            fontWeight: typ.displayWeight,
            letterSpacing: typ.displayLetter,
            color: c.ink,
          }}
        >
          {formatUsd(allTime.costUsd)}
        </div>
        <div
          className="mt-2 text-[12px]"
          style={{ color: c.inkSoft, fontFamily: typ.body }}
        >
          {formatNumber(allTime.scans)} scans · avg {formatUsd(avgCost, 4)}/scan · last 7d{' '}
          {formatUsd(last7Cost)}
        </div>
      </ChanceyCard>

      <div className="grid grid-cols-4 gap-2">
        <Stat label="Users" value={formatNumber(allTime.uniqueUsers)} />
        <Stat label="Scans" value={formatCompact(allTime.scans)} />
        <Stat label="Success" value={allTime.scans ? `${successRate}%` : '—'} />
        <Stat label="Errors" value={formatNumber(totalErrors)} />
      </div>

      <ChanceyCard c={c} pad={14}>
        <div className="mb-3 flex items-baseline justify-between">
          <div
            className="text-[13px] font-semibold"
            style={{ color: c.ink, fontFamily: typ.body }}
          >
            30-day activity
          </div>
          <div
            className="text-[11px]"
            style={{ color: c.inkMuted, fontFamily: typ.body }}
          >
            ok vs failed per day
          </div>
        </div>
        <DailyBars days={last30Days} />
      </ChanceyCard>

      <ChanceyCard c={c} pad={14}>
        <div
          className="mb-3 text-[13px] font-semibold"
          style={{ color: c.ink, fontFamily: typ.body }}
        >
          Top users
        </div>
        {topUsers.length === 0 ? (
          <Empty text="No scan activity yet." />
        ) : (
          <div className="flex flex-col">
            {topUsers.map((u) => (
              <div
                key={u.userId}
                className="flex items-center gap-3 py-2"
                style={{ borderTop: `0.5px solid ${c.line}` }}
              >
                <div className="min-w-0 flex-1">
                  <button
                    type="button"
                    className="block w-full truncate text-left font-mono text-[12px]"
                    style={{
                      color: c.ink,
                      fontFamily: typ.mono,
                      background: 'transparent',
                      border: 0,
                      padding: 0,
                    }}
                    // Copy full Clerk id on click — keeps the display screenshot-safe
                    // (only truncated id shown) but lets admins paste into Clerk
                    // Dashboard / logs search for real debugging.
                    onClick={() => {
                      try {
                        void navigator.clipboard?.writeText(u.userId);
                      } catch {
                        /* ignore - older browsers */
                      }
                    }}
                    title={`Click to copy: ${u.userId}`}
                    aria-label={`User id ${u.userId}. Click to copy.`}
                  >
                    {shortenId(u.userId)}
                  </button>
                  <div
                    className="mt-0.5 text-[11px]"
                    style={{ color: c.inkMuted, fontFamily: typ.body }}
                  >
                    {formatNumber(u.scans)} scans · last {relativeTime(u.lastSeen)}
                  </div>
                </div>
                <div
                  className="shrink-0 font-mono text-[13px] font-semibold"
                  style={{ color: c.ink, fontFamily: typ.mono }}
                >
                  {formatUsd(u.costUsd, 3)}
                </div>
              </div>
            ))}
          </div>
        )}
      </ChanceyCard>

      <ChanceyCard c={c} pad={14}>
        <div
          className="mb-3 text-[13px] font-semibold"
          style={{ color: c.ink, fontFamily: typ.body }}
        >
          Errors by code
        </div>
        {errorsByCode.length === 0 ? (
          <Empty text="No errors recorded." />
        ) : (
          <ErrorBars rows={errorsByCode} />
        )}
      </ChanceyCard>
    </>
  );
};

const Footer: React.FC<{ data: AdminOverview }> = ({ data }) => {
  const { c, typ } = useChanceyTheme();
  const p = data.pricing;
  // Format each rate with the minimum precision it needs — $2.50 stays "2.50",
  // $0.015 stays "0.015". Using toFixed(2) on everything truncated sub-cent
  // cached rates to "0.00" which was misleading.
  const fmt = (n: number): string => {
    if (n >= 1) return n.toFixed(2);
    if (n >= 0.01) return n.toFixed(3);
    return n.toFixed(4);
  };
  return (
    <div
      className="mt-1 text-center text-[10px] leading-snug"
      style={{ color: c.inkMuted, fontFamily: typ.body }}
    >
      <div>
        {p.model} · ${fmt(p.inputUsdPerMtok)}/Mtok in · ${fmt(p.cachedInputUsdPerMtok)}/Mtok
        cached · ${fmt(p.outputUsdPerMtok)}/Mtok out
      </div>
      {!p.resolvedFromModelTable && (
        <div className="mt-0.5" style={{ color: c.lucky }}>
          ⚠ Using env-override pricing. Remove OPENAI_*_USD_PER_MTOK vars to fall back to the
          built-in per-model table.
        </div>
      )}
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  const { c, typ } = useChanceyTheme();
  return (
    <ChanceyCard c={c} pad={12}>
      <div
        className="text-[10px] font-medium uppercase tracking-[0.08em]"
        style={{ color: c.inkMuted, fontFamily: typ.body }}
      >
        {label}
      </div>
      <div
        className="mt-1 font-mono text-[18px] font-semibold"
        style={{ color: c.ink, fontFamily: typ.mono }}
      >
        {value}
      </div>
    </ChanceyCard>
  );
};

const DailyBars: React.FC<{ days: AdminOverview['last30Days'] }> = ({ days }) => {
  const { c } = useChanceyTheme();
  const max = Math.max(1, ...days.map((d) => d.scans));
  return (
    <div className="flex h-[80px] items-end gap-[3px]">
      {days.map((day) => {
        const totalPct = (day.scans / max) * 100;
        const okShare = day.scans ? day.okScans / day.scans : 0;
        return (
          <div
            key={day.day}
            className="flex flex-1 flex-col justify-end"
            style={{ height: '100%' }}
            title={`${day.day}\n${day.scans} scans · ${day.okScans} ok · $${day.costUsd.toFixed(3)}`}
          >
            {day.scans === 0 ? (
              <div className="w-full rounded-sm" style={{ height: 2, background: c.line }} />
            ) : (
              <div
                className="flex w-full flex-col overflow-hidden rounded-sm"
                style={{ height: `${totalPct}%` }}
              >
                <div
                  className="w-full"
                  style={{ flex: 1 - okShare, background: c.lucky, opacity: 0.8 }}
                />
                <div
                  className="w-full"
                  style={{ flex: okShare || 0.0001, background: c.accent }}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const ErrorBars: React.FC<{ rows: Array<{ code: string; count: number }> }> = ({ rows }) => {
  const { c, typ } = useChanceyTheme();
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="flex flex-col gap-2">
      {rows.map((err) => (
        <div key={err.code} className="flex items-center gap-3">
          <div
            className="min-w-[100px] truncate font-mono text-[12px]"
            style={{ color: c.ink, fontFamily: typ.mono }}
          >
            {err.code}
          </div>
          <div
            className="relative h-1.5 flex-1 overflow-hidden rounded-sm"
            style={{ background: c.surfaceAlt }}
          >
            <div
              className="h-full rounded-sm"
              style={{ width: `${(err.count / max) * 100}%`, background: c.ink, opacity: 0.75 }}
            />
          </div>
          <div
            className="min-w-[40px] text-right font-mono text-[12px] font-semibold"
            style={{ color: c.ink, fontFamily: typ.mono }}
          >
            {err.count}
          </div>
        </div>
      ))}
    </div>
  );
};

const LoadingRow: React.FC = () => {
  const { c, typ } = useChanceyTheme();
  return (
    <div
      className="py-10 text-center text-[12px]"
      style={{ color: c.inkMuted, fontFamily: typ.body }}
    >
      Loading overview…
    </div>
  );
};

const ErrorCard: React.FC<{ error: string; onRetry: () => void }> = ({ error, onRetry }) => {
  const { c, typ } = useChanceyTheme();
  return (
    <ChanceyCard c={c} pad={16}>
      <div
        className="text-[13px] font-semibold"
        style={{ color: c.lucky, fontFamily: typ.body }}
      >
        Couldn&apos;t load overview
      </div>
      <div
        className="mt-1 text-[12px]"
        style={{ color: c.inkSoft, fontFamily: typ.body }}
      >
        {error}
      </div>
      <button
        type="button"
        onClick={onRetry}
        className="mt-3 rounded-full border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.06em]"
        style={{
          borderColor: c.line,
          color: c.ink,
          background: c.surfaceAlt,
          fontFamily: typ.body,
        }}
      >
        Try again
      </button>
    </ChanceyCard>
  );
};

const Empty: React.FC<{ text: string }> = ({ text }) => {
  const { c, typ } = useChanceyTheme();
  return (
    <div
      className="rounded-[12px] px-3 py-4 text-center text-[12px]"
      style={{ background: c.surfaceAlt, color: c.inkMuted, fontFamily: typ.body }}
    >
      {text}
    </div>
  );
};

function formatUsd(value: number, decimals = 2): string {
  if (!Number.isFinite(value) || value === 0) return '$0.00';
  const dp = Math.abs(value) < 0.01 ? Math.max(decimals, 4) : decimals;
  return `$${value.toFixed(dp)}`;
}

function formatNumber(n: number): string {
  return n.toLocaleString('en-US');
}

function formatCompact(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}

function relativeTime(iso: string | null): string {
  if (!iso) return 'never';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return 'unknown';
  const diff = Date.now() - then;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  const days = Math.floor(diff / 86_400_000);
  if (days < 30) return `${days}d ago`;
  return new Date(then).toISOString().slice(0, 10);
}

/** Shorten a Clerk-style id for display: `user_3ChCz…TayzdfiCGhtTayzdfi` → `user_…Tayzdfi`. */
function shortenId(id: string): string {
  if (id.length <= 14) return id;
  return `${id.slice(0, 5)}…${id.slice(-7)}`;
}

export default AdminPage;
